/**
 * Scheme-optional URL handling, mirroring the backend normalizer
 * (`shared/domain/profile-url.ts`) so the two never diverge: a bare domain is
 * accepted everywhere a link is collected, and normalized to HTTPS.
 */

const HAS_SCHEME = /^[a-z][a-z\d+.-]*:/i;
/** Backslashes and control/space characters are never valid in a link. */
const hasUnsafeCharacters = (value: string): boolean =>
  value.includes("\\") ||
  [...value].some((character) => character.charCodeAt(0) <= 0x20);

export function normalizeProfileUrl(value: string): string {
  const text = value.trim();
  if (!text || text.length > 2048 || hasUnsafeCharacters(text)) {
    throw new Error("Enter a valid website address");
  }
  const url = new URL(HAS_SCHEME.test(text) ? text : `https://${text}`);
  if (
    !["http:", "https:"].includes(url.protocol) ||
    url.username ||
    url.password ||
    !url.hostname.includes(".")
  ) {
    throw new Error("Enter a valid website address");
  }
  url.hash = "";
  return url.toString();
}

/** Null when the value cannot be normalized, so callers show one message. */
export function tryNormalizeProfileUrl(value: string): string | null {
  try {
    return normalizeProfileUrl(value);
  } catch {
    return null;
  }
}

export const isProfileUrlValidOrEmpty = (value: string): boolean =>
  value.trim() === "" || tryNormalizeProfileUrl(value) !== null;

export function normalizeLinkedInUrl(value: string): string {
  const url = new URL(normalizeProfileUrl(value));
  if (
    !["linkedin.com", "www.linkedin.com"].includes(url.hostname) ||
    !/^\/in\/[^/]+\/?$/.test(url.pathname)
  ) {
    throw new Error("Enter a LinkedIn /in/ profile URL");
  }
  return `https://www.linkedin.com${url.pathname.replace(/\/$/, "")}/`;
}

export function normalizeScholarUrl(value: string): string {
  const url = new URL(normalizeProfileUrl(value));
  const user = url.searchParams.get("user") ?? "";
  if (
    url.hostname !== "scholar.google.com" ||
    url.pathname !== "/citations" ||
    !/^[\w-]+$/.test(user)
  ) {
    throw new Error("Enter a Google Scholar author profile URL");
  }
  return `https://scholar.google.com/citations?user=${user}`;
}

const validOrEmpty =
  (normalize: (value: string) => string) => (value: string) => {
    if (value.trim() === "") return true;
    try {
      normalize(value);
      return true;
    } catch {
      return false;
    }
  };

export const isLinkedInValidOrEmpty = validOrEmpty(normalizeLinkedInUrl);
export const isScholarValidOrEmpty = validOrEmpty(normalizeScholarUrl);
